import type { CostoForecast, CostoTotalesMes } from "../../types";
import { FAMILIAS, fmtClpCorto, mesCorto } from "./costosFormat";

interface Props {
  meses: string[];
  totales: Record<string, CostoTotalesMes>;
  actual: string;
  forecast: CostoForecast;
  selected: string;
  onSelect: (mes: string) => void;
}

const W = 760;
const H = 260;
const PLOT_L = 56;
const PLOT_T = 14;
const PLOT_B = H - 26;

function techo(max: number): number {
  const pow = 10 ** Math.floor(Math.log10(Math.max(max, 1)));
  return Math.ceil(max / pow) * pow;
}

/** Escritorio: barras apiladas por familia con eje, líneas guía y total sobre cada barra; el mes en curso suma una barra punteada con la proyección. */
export function CostosChart({ meses, totales, actual, forecast, selected, onSelect }: Props) {
  const n = meses.length;
  const step = (W - PLOT_L - 8) / n;
  const bw = Math.min(step * 0.56, 56);
  const xOf = (i: number) => PLOT_L + step * (i + 0.5);
  const proyectado = forecast.completo || forecast.total > (totales[actual]?.total ?? 0) ? forecast.total : 0;
  const max = techo(Math.max(1, ...meses.map((m) => Math.max(totales[m]?.total ?? 0, m === actual ? proyectado : 0))));
  const yOf = (v: number) => PLOT_B - (v / max) * (PLOT_B - PLOT_T);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);

  return (
    <div className="cst-chart">
      <svg className="sbx-svg" viewBox={`0 0 ${W} ${H}`} width="100%" role="group" aria-label="Costo mensual por familia">
        {ticks.map((t) => (
          <g key={t}>
            <line className={t === 0 ? "base" : "grid"} x1={PLOT_L} x2={W - 8} y1={yOf(t)} y2={yOf(t)} />
            <text x={PLOT_L - 6} y={yOf(t) + 4} textAnchor="end">{fmtClpCorto(t)}</text>
          </g>
        ))}
        {meses.map((m, i) => {
          const t = totales[m];
          let acc = 0;
          const x = xOf(i) - bw / 2;
          return (
            <g key={m}>
              <rect className={m === selected ? "cst-hit on" : "cst-hit"} x={xOf(i) - step / 2} y={PLOT_T} width={step} height={PLOT_B - PLOT_T + 24} onClick={() => onSelect(m)} tabIndex={0} role="button" aria-label={`Ver ${mesCorto(m)}`} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(m)} />
              {FAMILIAS.map((f) => {
                const v = t?.[f.key] ?? 0;
                if (v <= 0) return null;
                const y = yOf(acc + v);
                const h = yOf(acc) - y;
                acc += v;
                return <rect key={f.key} className={`cst-bar cst-${f.key}`} x={x} y={y} width={bw} height={Math.max(h, 1)} />;
              })}
              {m === actual && proyectado > acc && <rect className="cst-forecast" x={x} y={yOf(proyectado)} width={bw} height={yOf(acc) - yOf(proyectado)} />}
              {t && t.total > 0 && (
                <text className="strong" x={xOf(i)} y={yOf(Math.max(acc, m === actual ? proyectado : 0)) - 6} textAnchor="middle">{fmtClpCorto(t.total)}</text>
              )}
              <text className={m === selected ? "strong" : undefined} x={xOf(i)} y={H - 8} textAnchor="middle">{mesCorto(m)}{m === actual ? "*" : ""}</text>
            </g>
          );
        })}
      </svg>
      <div className="sbx-chart-legend">
        {FAMILIAS.map((f) => <span key={f.key}><i className={`cst-dot cst-${f.key}`} />{f.short}</span>)}
        <span><i className="cst-dot cst-fc" />Proyección</span>
      </div>
    </div>
  );
}
